import type { TestPlayerDeactivateResult } from '../../lib/api/operatorConsole';
import { Badge, type BadgeTone } from './Badge';
import type { DeactivationReport } from './deactivationReport';

interface Props {
  report: DeactivationReport;
  onDismiss: () => void;
}

const STATUS_LABEL: Record<string, string> = {
  deactivated: 'Removed',
  forbidden: 'Refused',
  already_inactive: 'Was already off the list',
  not_present: 'Not on the list',
  invalid: 'Could not be read',
};

const STATUS_TONE: Record<string, BadgeTone> = {
  deactivated: 'success',
  forbidden: 'error',
  already_inactive: 'neutral',
  not_present: 'neutral',
  invalid: 'error',
};

// Only the backend knows why a row was refused, so its `message` is printed as
// it came. The fallbacks below state the status and nothing more — they never
// invent a reason.
const STATUS_DETAIL: Record<string, string> = {
  already_inactive: 'This id was not being excluded when the request arrived.',
  not_present: 'This id is not on the registry.',
};

function detail(row: TestPlayerDeactivateResult): string {
  return row.message ?? STATUS_DETAIL[row.status] ?? '';
}

export function DeactivateResultPanel({ report, onDismiss }: Props) {
  const counts = [
    `${report.deactivated} removed`,
    `${report.refused} refused`,
    ...(report.unresolved ? [`${report.unresolved} not applicable`] : []),
  ].join(' • ');

  return (
    <section
      aria-label="Deactivation result"
      className="mb-5 max-w-3xl rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-4"
    >
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h2 className="text-[13px] font-semibold text-[var(--co-text)]">
          Deactivation result —{' '}
          <span className="font-normal text-[var(--co-muted)]">{counts}</span>
        </h2>
        <button
          type="button"
          onClick={onDismiss}
          className="text-[11px] font-medium text-[var(--co-muted)] hover:text-[var(--co-text)]"
        >
          Dismiss
        </button>
      </div>

      {report.refused > 0 && (
        <p
          className="mb-3 rounded-lg px-3 py-2 text-[12px]"
          style={{
            color: 'var(--co-error)',
            backgroundColor: 'color-mix(in srgb, var(--co-error) 10%, transparent)',
          }}
        >
          <strong className="font-semibold">
            {report.refused} mark{report.refused === 1 ? '' : 's'} stayed in place.
          </strong>{' '}
          Those accounts are still excluded from your reporting and from settlement.
        </p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-[var(--co-border)]">
              <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--co-muted)]">
                Player id
              </th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--co-muted)]">
                Result
              </th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--co-muted)]">
                Details
              </th>
            </tr>
          </thead>
          <tbody>
            {report.rows.map((row, index) => (
              <tr
                key={`${row.submitted}-${index}`}
                className="border-b border-[var(--co-border)] last:border-0"
              >
                <td className="px-3 py-2 font-mono text-[12px] text-[var(--co-text)]">
                  {row.account_id || row.submitted}
                </td>
                <td className="px-3 py-2">
                  <Badge tone={STATUS_TONE[row.status] ?? 'neutral'}>
                    {STATUS_LABEL[row.status] ?? row.status}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-[12px] text-[var(--co-muted)]">
                  {detail(row) || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
